import { useState, useRef, useEffect, useCallback } from "react";
import { X, Send, Loader2, Minimize2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useFocusTrap } from "@/hooks/useFocusTrap";
import chatAvatar from "@/assets/chat-avatar.jpg";

interface Message {
  role: "user" | "assistant";
  content: string;
}

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/scholarship-chat`;

const SUGGESTED_QUESTIONS = [
  "What are the eligibility requirements?",
  "What documents do I need?",
  "What is the Tapia Family Award?",
  "Can I apply if I'm undocumented?",
];

export function ScholarshipChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [focusedMessageIndex, setFocusedMessageIndex] = useState<number>(-1);
  const [sessionId] = useState(() => crypto.randomUUID()); // Stable session ID for logging
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerButtonRef = useRef<HTMLButtonElement>(null);
  const messageRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Focus trap for accessibility - traps focus within chat when open
  const { containerRef: focusTrapRef } = useFocusTrap(isOpen, () => setIsOpen(false));

  // Handle arrow key navigation for messages
  const handleMessagesKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (messages.length === 0) return;
    
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedMessageIndex(prev => {
        const newIndex = prev <= 0 ? messages.length - 1 : prev - 1;
        messageRefs.current[newIndex]?.focus();
        return newIndex;
      });
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedMessageIndex(prev => {
        const newIndex = prev >= messages.length - 1 ? 0 : prev + 1;
        messageRefs.current[newIndex]?.focus();
        return newIndex;
      });
    } else if (e.key === 'Home') {
      e.preventDefault();
      setFocusedMessageIndex(0);
      messageRefs.current[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      const lastIndex = messages.length - 1;
      setFocusedMessageIndex(lastIndex);
      messageRefs.current[lastIndex]?.focus();
    }
  }, [messages.length]);

  // Reset message refs when messages change
  useEffect(() => {
    messageRefs.current = messageRefs.current.slice(0, messages.length);
  }, [messages.length]);

  // Handle closing with focus return
  const handleClose = useCallback((clearMessages = false) => {
    setIsOpen(false);
    if (clearMessages) {
      setMessages([]);
    }
    // Focus returns automatically via useFocusTrap
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages]);

  // Focus input when chat opens (after focus trap activates)
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const timer = setTimeout(() => inputRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const streamChat = useCallback(async (userMessages: Message[]) => {
    const resp = await fetch(CHAT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
      },
      body: JSON.stringify({ messages: userMessages, sessionId }),
    });

    if (!resp.ok) {
      const errorData = await resp.json().catch(() => ({}));
      throw new Error(errorData.error || "Failed to get response");
    }

    if (!resp.body) throw new Error("No response body");

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let textBuffer = "";
    let assistantContent = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      textBuffer += decoder.decode(value, { stream: true });

      let newlineIndex: number;
      while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
        let line = textBuffer.slice(0, newlineIndex);
        textBuffer = textBuffer.slice(newlineIndex + 1);

        if (line.endsWith("\r")) line = line.slice(0, -1);
        if (line.startsWith(":") || line.trim() === "") continue;
        if (!line.startsWith("data: ")) continue;

        const jsonStr = line.slice(6).trim();
        if (jsonStr === "[DONE]") break;

        try {
          const parsed = JSON.parse(jsonStr);
          const content = parsed.choices?.[0]?.delta?.content as string | undefined;
          if (content) {
            assistantContent += content;
            setMessages(prev => {
              const last = prev[prev.length - 1];
              if (last?.role === "assistant") {
                return prev.map((m, i) => 
                  i === prev.length - 1 ? { ...m, content: assistantContent } : m
                );
              }
              return [...prev, { role: "assistant", content: assistantContent }];
            });
          }
        } catch {
          textBuffer = line + "\n" + textBuffer;
          break;
        }
      }
    }

    return assistantContent;
  }, []);

  const handleSend = async (messageText?: string) => {
    const text = messageText || input.trim();
    if (!text || isLoading) return;

    const userMsg: Message = { role: "user", content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setIsLoading(true);

    try {
      await streamChat([...messages, userMsg]);
    } catch (error) {
      console.error("Chat error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to send message");
      // Remove the last user message if there was an error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestedQuestion = (question: string) => {
    handleSend(question);
  };

  return (
    <>
      {/* Floating Button - serves as trigger for focus return */}
      <button
        ref={triggerButtonRef}
        onClick={() => setIsOpen(true)}
        className={cn(
          "fixed bottom-6 right-6 z-50 flex items-center justify-center",
          "w-14 h-14 rounded-full shadow-lg overflow-hidden",
          "hover:scale-105 transition-all duration-200",
          "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
          "border-2 border-primary",
          isOpen && "hidden"
        )}
        aria-label="Open chat assistant"
        aria-expanded={isOpen}
        aria-controls="chat-widget-panel"
      >
        <img src={chatAvatar} alt="" className="w-full h-full object-cover" width={52} height={52} loading="lazy" decoding="async" />
      </button>

      {/* Chat Window - with focus trap */}
      <div
        ref={focusTrapRef}
        id="chat-widget-panel"
        role="dialog"
        aria-modal="true"
        aria-label="CTSF Scholarship Chat Assistant"
        className={cn(
          "fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)]",
          "bg-background border border-border rounded-xl shadow-2xl",
          "flex flex-col overflow-hidden transition-all duration-300",
          isOpen ? "h-[500px] opacity-100 scale-100" : "h-0 opacity-0 scale-95 pointer-events-none"
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground">
          <div className="flex items-center gap-3">
            <Avatar className="w-9 h-9 border-2 border-primary-foreground/20">
              <AvatarImage src={chatAvatar} alt="CTSF Assistant" />
              <AvatarFallback>CA</AvatarFallback>
            </Avatar>
            <div>
              <h3 className="font-semibold text-sm">CTSF Assistant</h3>
              <p className="text-xs opacity-90">Ask about scholarships</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/10"
              onClick={() => handleClose(false)}
              aria-label="Minimize chat"
            >
              <Minimize2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/10"
              onClick={() => handleClose(true)}
              aria-label="Close chat and clear history"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Messages */}
        <ScrollArea ref={scrollAreaRef} className="flex-1 p-4">
          {messages.length === 0 ? (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  Hi! I'm your CTSF scholarship assistant. How can I help you today?
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground font-medium">Suggested questions:</p>
                {SUGGESTED_QUESTIONS.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestedQuestion(question)}
                    className="w-full text-left text-sm px-3 py-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                    aria-label={`Ask: ${question}`}
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div 
              className="space-y-4" 
              aria-live="polite" 
              aria-atomic="false"
              role="log"
              aria-label="Chat messages"
              onKeyDown={handleMessagesKeyDown}
            >
              {messages.map((msg, index) => (
                <div
                  key={index}
                  ref={el => messageRefs.current[index] = el}
                  tabIndex={0}
                  role="article"
                  aria-label={`${msg.role === 'user' ? 'You' : 'Assistant'}: ${msg.content}`}
                  className={cn(
                    "flex outline-none",
                    "focus:ring-2 focus:ring-primary focus:ring-offset-1 rounded-lg",
                    msg.role === "user" ? "justify-end" : "justify-start"
                  )}
                  onFocus={() => setFocusedMessageIndex(index)}
                >
                  {msg.role === "assistant" && (
                    <Avatar className="w-7 h-7 shrink-0">
                      <AvatarImage src={chatAvatar} alt="Assistant" />
                      <AvatarFallback>CA</AvatarFallback>
                    </Avatar>
                  )}
                  <div
                    className={cn(
                      "max-w-[80%] rounded-lg px-3 py-2 text-sm",
                      msg.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-foreground"
                    )}
                  >
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              {isLoading && messages[messages.length - 1]?.role === "user" && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-lg px-3 py-2">
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  </div>
                </div>
              )}
            </div>
          )}
        </ScrollArea>

        {/* Input */}
        <div className="p-3 border-t border-border">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your question..."
              disabled={isLoading}
              className="flex-1"
              aria-label="Type your message"
            />
            <Button
              onClick={() => handleSend()}
              disabled={!input.trim() || isLoading}
              size="icon"
              className="shrink-0"
              aria-label="Send message"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
          <div className="flex items-center justify-between mt-2">
            <p className="text-[10px] text-muted-foreground">
              AI responses may not always be accurate.
            </p>
            {messages.length > 0 && (
              <button
                onClick={() => setMessages([])}
                disabled={isLoading}
                className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors disabled:opacity-50"
                aria-label="Clear chat history"
              >
                <RotateCcw className="w-3 h-3" />
                Clear chat
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
